import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const { verifyEmail } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') || '';
  const codeParam = searchParams.get('code') || '';

  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState(codeParam);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  // Auto-verify if code is in URL
  useEffect(() => {
    if (emailParam && codeParam) {
      handleVerify();
    }
  }, []);

  const handleVerify = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyEmail(email, code);
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8" data-testid="verify-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-[#22c55e]" />
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-4">
            {language === 'es' ? '¡Email Verificado!' : 'Email Verified!'}
          </h2>
          <p className="text-slate-600 mb-8">
            {language === 'es' 
              ? 'Tu cuenta ha sido verificada. Redirigiendo al login...'
              : 'Your account has been verified. Redirecting to login...'}
          </p>
          <Link
            to="/login"
            className="vf-btn-primary inline-flex items-center justify-center gap-2"
          >
            {language === 'es' ? 'Ir al Login' : 'Go to Login'}
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-8" data-testid="verify-email-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#1e3a5f] mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'es' ? 'Volver al login' : 'Back to login'}
        </Link>

        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-[#1e3a5f] rounded-sm flex items-center justify-center">
            <span className="text-white font-bold text-2xl">V</span>
          </div>
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-2">
            {language === 'es' ? 'Verificar Email' : 'Verify Email'}
          </h2>
          <p className="text-slate-600">
            {language === 'es' 
              ? 'Introduce el código que te enviamos por email'
              : 'Enter the code we sent to your email'}
          </p>
        </div>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </motion.div>
        )}

        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="vf-input w-full px-4 py-3"
              placeholder="tu@email.com"
              data-testid="verify-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
              {language === 'es' ? 'Código de Verificación' : 'Verification Code'}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              className="vf-input w-full px-4 py-3 text-center text-2xl tracking-widest"
              placeholder="000000"
              data-testid="verify-code-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="vf-btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="verify-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              language === 'es' ? 'Verificar' : 'Verify'
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-600">
          {language === 'es' ? '¿No recibiste el código?' : "Didn't receive the code?"}
          {' '}
          <button 
            className="text-[#22c55e] hover:underline disabled:opacity-50"
            disabled={resending || !email}
            data-testid="resend-code-btn"
            onClick={async () => {
              if (!email) return;
              setResending(true);
              setResendMsg('');
              try {
                const res = await fetch(`${API}/api/auth/resend-verification`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email }),
                });
                const data = await res.json();
                setResendMsg(language === 'es' ? 'Código reenviado. Revisa tu email.' : 'Code resent. Check your email.');
              } catch (e) {
                setResendMsg(language === 'es' ? 'Error al reenviar' : 'Failed to resend');
              } finally {
                setResending(false);
              }
            }}
          >
            {resending ? (language === 'es' ? 'Enviando...' : 'Sending...') : (language === 'es' ? 'Reenviar' : 'Resend')}
          </button>
          {resendMsg && <span className="block mt-1 text-[#22c55e]">{resendMsg}</span>}
        </p>
      </motion.div>
    </div>
  );
};

export default VerifyEmailPage;
